/** @jsxImportSource @emotion/react */

'use client';

import { css } from '@emotion/react';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { Box, Stack, Step, StepLabel, Tooltip } from '@mui/material';
import { amber } from '@mui/material/colors';
import MuiStepper from '@mui/material/Stepper';
import type { MouseEvent, ReactNode } from 'react';

import { Link, Text } from '@campaign-manager/react/components';
import { campaignManagerTheme } from '@campaign-manager/theme';

const styles = {
  root: css`
    height: 100%;
    display: flex;
    flex-direction: column;
  `,
  itemsContainer: css`
    padding-top: 28px;
    padding-left: 38px;
    flex: 1 1 auto;
    overflow-y: auto;
  `,
  stepButton: css`
    border: none;
    background-color: transparent;
    padding: 0;
    cursor: pointer;

    :disabled {
      cursor: default;
    }
  `,
  mutedLink: css`
    color: ${campaignManagerTheme.color.grey};

    :hover {
      color: ${campaignManagerTheme.color.black};
    }
  `,
  stepper: css`
    .MuiStepConnector-root {
      margin-left: 5px;
    }
    .MuiStepConnector-line {
      min-height: 48px;
    }
  `,
};

export interface WizardStepperProps {
  /** Current active step index */
  activeStep: number;
  /** Array of step labels */
  steps: string[];
  /** Callback when a step is clicked */
  onStepChange: (step: number) => void;
  /** Whether to show confirmation dialog when navigating away */
  shouldConfirmChange: boolean;
  /** Callback to trigger confirmation dialog, should return Promise<void> that resolves on confirm, rejects on cancel */
  confirmNavigation: () => Promise<void>;
  /** Callback when cancel is clicked (after confirmation if needed) */
  onCancel: () => void;
  /** Set of step indices that have pending changes awaiting approval */
  stepsWithPendingChanges?: Set<number>;
  /** Optional support element to display at the bottom of the stepper */
  supportElement?: ReactNode;
}

/**
 * WizardStepper - A grid-aware stepper component for wizard layouts
 *
 * This component is designed to work within the WizardLayout sidebar slot.
 * It doesn't include a Drawer or manage its own positioning - that's handled
 * by the parent WizardLayout grid.
 */
export default function WizardStepper({
  activeStep,
  steps,
  shouldConfirmChange,
  confirmNavigation,
  onStepChange,
  onCancel,
  stepsWithPendingChanges,
  supportElement,
}: WizardStepperProps) {
  const handleStepButtonClick = async (e: MouseEvent<HTMLButtonElement>, step: number) => {
    if (shouldConfirmChange) {
      e.preventDefault();
      e.stopPropagation();
      try {
        await confirmNavigation();
      } catch {
        return;
      }
    }

    onStepChange(step);
  };

  const handleCancelClick = async (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (shouldConfirmChange) {
      try {
        await confirmNavigation();
      } catch {
        return;
      }
    }

    onCancel();
  };

  return (
    <div css={styles.root}>
      <Stack
        css={styles.itemsContainer}
        sx={{
          gap: 4,
          alignItems: 'start',
        }}
      >
        <Link
          component="button"
          onClick={handleCancelClick}
          css={styles.mutedLink}
        >
          <Stack
            direction="row"
            sx={{
              gap: 1,
              alignItems: 'center',
            }}
          >
            <KeyboardBackspaceIcon fontSize="inherit" />
            Cancel
          </Stack>
        </Link>
        <nav aria-label="Wizard steps">
          <MuiStepper
            css={styles.stepper}
            activeStep={activeStep}
            orientation="vertical"
          >
            {steps.map((step, index) => (
              <Step key={step}>
                <button
                  type="button"
                  css={styles.stepButton}
                  disabled={activeStep < index}
                  aria-current={activeStep === index ? 'step' : undefined}
                  onClick={(e) => handleStepButtonClick(e, index)}
                >
                  <StepLabel
                    style={{ padding: 0, margin: '-5px 0' }}
                    slots={{ stepIcon: StepIcon }}
                  >
                    <Stack
                      direction="row"
                      sx={{
                        alignItems: 'center',
                        gap: 0.75,
                      }}
                    >
                      {step}
                      {stepsWithPendingChanges?.has(index) && (
                        <Tooltip title="Pending retailer approval">
                          <span
                            style={{
                              display: 'inline-block',
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              backgroundColor: amber[500],
                              flexShrink: 0,
                            }}
                            aria-label="This step has changes pending retailer approval"
                          />
                        </Tooltip>
                      )}
                    </Stack>
                  </StepLabel>
                </button>
              </Step>
            ))}
          </MuiStepper>
        </nav>
      </Stack>
      {supportElement && <Box sx={{ px: 4.75, pb: 4 }}>{supportElement}</Box>}
    </div>
  );
}

function StepIcon({ active, completed }: { active?: boolean; completed?: boolean }) {
  const getBackgroundColor = () => {
    if (completed) return campaignManagerTheme.color.green;
    if (active) return campaignManagerTheme.color.secondary;
    return 'transparent';
  };

  const getBorderColor = () => {
    if (completed) return campaignManagerTheme.color.green;
    if (active) return campaignManagerTheme.color.secondary;
    return campaignManagerTheme.color.grey;
  };

  return (
    <div
      style={{
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        border: `1px solid ${getBorderColor()}`,
        backgroundColor: getBackgroundColor(),
      }}
    />
  );
}
